<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useMessagesStore } from '@/stores/messages'
import AvatarUpload from '@/components/AvatarUpload.vue'
import { api } from '../utils/axios'
import {markdown} from '../utils/helpers'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const messages = useMessagesStore()
const bio = ref(null)
const public_profile = ref(null)
const edit_mode = ref(false)

// Email management
const email = ref('')
const emailIsConfirmed = ref(false)
const emailMessage = ref('')
const emailError = ref('')
const emailLoading = ref(false)

// Verification banner
const verificationBanner = ref({
  show: false,
  type: '', // 'success' or 'error'
  message: ''
})

function closeVerificationBanner() {
  verificationBanner.value.show = false
}

async function handleLogout() {
  if (await authStore.logout()) {
    router.push('/login')
    messages.info('🔒 You are logged out.')
  } else {
    messages.warning('🐙 Something went wrong during logout.')
  }
}

function toggleEditMode() {
  edit_mode.value = !edit_mode.value
  api.get('/profile').then((response) => {
    bio.value = response.data.bio
    public_profile.value = response.data.public_profile
  })
}
function saveProfile() {
  if (edit_mode.value) {
    try {
      const response = api.post('/profile', {
        bio: bio.value,
        public_profile: public_profile.value,
      })
      messages.info('👍 Profile updated')
    } catch (error) {
      messages.error('❌ Error updating profile')
    }
  }
  edit_mode.value = !edit_mode.value
}


const renderedBio = computed(() => {
  if (!bio.value) return ''
  return markdown(bio.value)
})

const profileLinks = computed(() => {
  if (!public_profile.value) return []
  return public_profile.value.split('\n').filter((link) => link.trim()) // Odstraní prázdné řádky
})

// Email management functions
async function fetchEmailInfo() {
  try {
    const response = await api.get('/profile/email')
    email.value = response.data.email || ''
    emailIsConfirmed.value = response.data.email_is_confirmed || false
  } catch (error) {
    console.error('Error fetching email info:', error)
  }
}

async function updateEmail() {
  if (!email.value.trim()) {
    emailError.value = 'Enter email address'
    return
  }

  emailLoading.value = true
  emailError.value = ''
  emailMessage.value = ''

  try {
    const response = await api.post('/profile/email', { email: email.value })
    emailMessage.value = response.data.message
    emailIsConfirmed.value = false
    messages.success('✅ Email has been updated. Check your inbox.')
  } catch (error) {
    emailError.value = error.response?.data?.error || 'Error updating email'
    messages.error('❌ ' + emailError.value)
  } finally {
    emailLoading.value = false
  }
}

async function resendVerificationEmail() {
  emailLoading.value = true
  emailError.value = ''
  emailMessage.value = ''

  try {
    const response = await api.post('/profile/email/resend')
    emailMessage.value = response.data.message
    messages.success('✅ Verification email has been resent')
  } catch (error) {
    emailError.value = error.response?.data?.error || 'Error sending email'
    messages.error('❌ ' + emailError.value)
  } finally {
    emailLoading.value = false
  }
}

async function deleteEmail() {
  if (!confirm('Are you sure you want to remove your email address? You will no longer receive project notifications.')) {
    return
  }

  emailLoading.value = true
  emailError.value = ''
  emailMessage.value = ''

  try {
    const response = await api.delete('/profile/email')
    email.value = ''
    emailIsConfirmed.value = false
    emailMessage.value = response.data.message
    messages.success('✅ Email address has been removed')
  } catch (error) {
    emailError.value = error.response?.data?.error || 'Failed to remove email'
    messages.error('❌ ' + emailError.value)
  } finally {
    emailLoading.value = false
  }
}

onMounted(async () => {
  await authStore.getAvatarURL()
  const response = await api.get('/profile')
  bio.value = response.data.bio
  public_profile.value = response.data.public_profile

  // Fetch email info
  await fetchEmailInfo()

  // Check for verification status in URL query params
  if (route.query.verification_success) {
    verificationBanner.value = {
      show: true,
      type: 'success',
      message: route.query.verification_success
    }
    messages.success('✅ ' + route.query.verification_success)
    await fetchEmailInfo() // Refresh email status
    // Clear query params
    router.replace({ query: {} })
  } else if (route.query.verification_error) {
    verificationBanner.value = {
      show: true,
      type: 'error',
      message: route.query.verification_error
    }
    messages.error('❌ ' + route.query.verification_error)
    // Clear query params
    router.replace({ query: {} })
  }
})
</script>

<template>
  <main>
    <!-- Verification banner -->
    <div v-if="verificationBanner.show"
         :class="['verification-banner', verificationBanner.type]">
      <div class="banner-content">
        <span class="banner-icon">
          {{ verificationBanner.type === 'success' ? '✅' : '❌' }}
        </span>
        <span class="banner-message">{{ verificationBanner.message }}</span>
        <button @click="closeVerificationBanner" class="banner-close">×</button>
      </div>
    </div>

    <h2>Profil</h2>
    <div class="avatar">
      <button @click="handleLogout" class="logout-btn">Odhlásit se</button>
      <img v-if="authStore.avatarURL" :src="authStore.avatarURL" alt="Avatar" />
      <AvatarUpload />
    </div>
    <div class="profile">
      <p>User: {{ authStore.display_name }}</p>
      <p>UserName: &lt;{{ authStore.username }}&gt;</p>
      <p>Group: {{ authStore.groupname }}</p>
      <p>
        Admin:
        <span v-if="authStore.isAdmin">yes</span>
        <span v-else>no</span>
      </p>
    </div>
    <div v-if="edit_mode" class="warning">
      <div class="profile">
        <h3>Bio</h3>
        <textarea v-model="bio" placeholder="Bio"></textarea>
      </div>
      <div class="profile">
        <h3>Public profile</h3>
        <textarea v-model="public_profile" placeholder="https://myhub.cz/kitty"></textarea>
      </div>
    </div>
    <div v-else class="profile">
      <h3>Bio</h3>
      <div class="markdown-content" v-html="renderedBio"></div>
      <h3>Public profile</h3>
      <div class="links-list">
        <a v-for="(link, index) in profileLinks" :key="index" :href="link.trim()" target="_blank"
          rel="noopener noreferrer" class="profile-link">
          {{ link.trim() }}
        </a>
      </div>
    </div>

    <!-- Email management section -->
    <div class="profile email-section">
      <h3>📧 Email for Notifications</h3>

      <div v-if="!edit_mode" class="email-display">
        <div v-if="email && emailIsConfirmed" class="email-verified">
          <span class="status-icon">✅</span>
          <span class="email-text">{{ email }}</span>
          <span class="status-label">(verified)</span>
          <button @click="deleteEmail" class="remove-btn" :disabled="emailLoading">
            {{ emailLoading ? 'Removing...' : 'Remove' }}
          </button>
        </div>
        <div v-else-if="email && !emailIsConfirmed" class="email-unverified">
          <span class="status-icon">⚠️</span>
          <span class="email-text">{{ email }}</span>
          <span class="status-label">(unverified)</span>
          <button @click="resendVerificationEmail" class="resend-btn" :disabled="emailLoading">
            {{ emailLoading ? 'Sending...' : 'Resend verification email' }}
          </button>
          <button @click="deleteEmail" class="remove-btn" :disabled="emailLoading">
            {{ emailLoading ? 'Removing...' : 'Remove' }}
          </button>
        </div>
        <div v-else class="no-email">
          <span class="status-icon">📭</span>
          <span class="email-text">No email address is set</span>
        </div>
      </div>

      <div v-else class="email-edit">
        <label for="email-input">Email address:</label>
        <input
          id="email-input"
          v-model="email"
          type="email"
          placeholder="your.email@example.com"
          class="email-input"
        />
        <button @click="updateEmail" class="save-email-btn" :disabled="emailLoading">
          {{ emailLoading ? 'Saving...' : 'Save and verify email' }}
        </button>
        <p class="email-info">
          📧 By setting your email, you will automatically receive email notifications about project approvals and closures.
        </p>
      </div>

      <div v-if="emailMessage" class="email-message success">{{ emailMessage }}</div>
      <div v-if="emailError" class="email-message error">{{ emailError }}</div>
    </div>

    <div class="profile">
      <button @click="toggleEditMode" class="edit-btn">
        {{ edit_mode ? 'Cancel' : 'Edit' }}
      </button>
      <button v-if="edit_mode" @click="saveProfile" class="edit-btn">Save</button>
    </div>
  </main>
</template>

<style scoped>
/* Verification banner */
.verification-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.verification-banner.success {
  background-color: #28a745;
  color: white;
  border-bottom: 3px solid #1e7e34;
}

.verification-banner.error {
  background-color: #dc3545;
  color: white;
  border-bottom: 3px solid #bd2130;
}

.banner-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.banner-icon {
  font-size: 1.5rem;
}

.banner-message {
  flex: 1;
  font-size: 1.1rem;
  font-weight: 600;
}

.banner-close {
  background: transparent;
  border: 2px solid white;
  color: white;
  font-size: 1.5rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  transition: all 0.2s;
}

.banner-close:hover {
  background: white;
  color: inherit;
}

.warning {
  background-color: #f8d7da;
  color: #721c24;
  border-color: #f5c6cb;
}

textarea {
  width: 88em;
  max-width: calc(100% - 256px);
  height: 10rem;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
}

h3 {
  font-size: 1.7rem;
  font-weight: bold;
  margin-top: 2rem;
}

.avatar {
  float: right;
  width: 256px;
  text-align: center;
}

.avatar button {
  margin-top: 0rem;
  margin-bottom: 2rem;
}

.avatar img {
  border: solid black 3px;
  border-radius: 40%;
  margin-bottom: 1rem;
  width: 100%;
  height: auto;
}

.profile {
  max-width: 800px;
  margin: 0 auto;
  padding: 1ex 0ex;
}

.logout-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.logout-btn:hover {
  background: #c82333;
}

.links-list {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-left: 1rem;
}

.profile-link {
  color: #6655aa;
  text-decoration: none;
  transition: color 0.2s;
}

.profile-link:hover {
  color: #4a3d7d;
  text-decoration: underline;
}

.edit-btn {
  background: #6655aa;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
}

.edit-btn:hover {
  background: #357abd;
}

/* Email management section */
.email-section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;
  border-left: 4px solid #6655aa;
}

.email-display {
  margin-top: 1rem;
}

.email-verified,
.email-unverified,
.no-email {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.email-verified {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
}

.email-unverified {
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  flex-wrap: wrap;
}

.no-email {
  background-color: #e2e3e5;
  border: 1px solid #d6d8db;
}

.status-icon {
  font-size: 1.2rem;
}

.email-text {
  font-weight: 600;
  flex: 1;
}

.status-label {
  color: #666;
  font-size: 0.9em;
}

.resend-btn {
  background: #ffc107;
  color: #333;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  margin-left: auto;
}

.resend-btn:hover {
  background: #e0a800;
}

.resend-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.remove-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  margin-left: 0.5rem;
}

.remove-btn:hover {
  background: #c82333;
}

.remove-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.email-edit {
  margin-top: 1rem;
}

.email-edit label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.email-input {
  width: 100%;
  max-width: 400px;
  padding: 0.6rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  margin-bottom: 1rem;
}

.email-input:focus {
  outline: none;
  border-color: #6655aa;
  box-shadow: 0 0 0 2px rgba(102, 85, 170, 0.1);
}

.save-email-btn {
  background: #6655aa;
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95em;
  transition: background-color 0.2s;
}

.save-email-btn:hover {
  background: #554499;
}

.save-email-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.email-info {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #e7f3ff;
  border-left: 3px solid #2196f3;
  font-size: 0.9em;
  color: #0c5460;
}

.email-message {
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.9em;
}

.email-message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.email-message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}
</style>
